import { Injectable } from '@angular/core';
import { MapLayersManager } from '../map-layers-manager';
import { FeatureCollection } from 'geojson';
import { LocationApiService } from '../../../../data/location-api/location-api.service';
import { Subscription } from 'rxjs';
import { RoutesManagerService } from '../../../../data/routes/routes-manager.service';
import { RouteSegmentStatusLayerService } from './route-segment-status-layer.service';

@Injectable({
  providedIn: 'root',
})
export class RouteSegmentStatusSourceService {
  static readonly ROUTE_SEGMENT_SOURCE_ID = 'route-segment-status-source';

  private mapLayersManager: MapLayersManager;
  private isEnabled: boolean;
  private routeSegmentGeometries: FeatureCollection;
  private routeConfigurationId: number;
  private routeIds: number[];
  private sourceUpdateIntervalId: NodeJS.Timeout;
  private readonly openSubscriptions = Array<Subscription>();

  constructor(
    private readonly locationApiService: LocationApiService,
    private readonly routesManager: RoutesManagerService,
    private readonly routeSegmentStatusLayer: RouteSegmentStatusLayerService,
  ) { }

  init(mapLayersManager: MapLayersManager, isEnabled: boolean) {
    if (this.mapLayersManager) {
      throw Error('The map layers manager has already been set.');
    }
    this.isEnabled = isEnabled;
    this.mapLayersManager = mapLayersManager;
    this.addSource();

    if (this.isEnabled) {
      const routesFilterSubscription = this.routesManager.routeFilter$.subscribe(filterUpdate => {
        this.routeConfigurationId = filterUpdate.configId;
        this.routeIds = filterUpdate.routeIds;
        if (this.routeConfigurationId) {
          this.updateSource();
        }
      });
      this.openSubscriptions.push(routesFilterSubscription);

      // regularly update only when the Route Status theme is active / Route Segment Status layer is visible
      this.sourceUpdateIntervalId = setInterval(() => {
        if (this.routeSegmentStatusLayer.getLayerVisibility()) {
          this.updateSource();
        }
      }, 30 * 1000);
    }
  }

  release() {
    if (!this.mapLayersManager) {
      throw Error('The map has not been set!');
    }
    this.mapLayersManager = null;

    if (!!this.sourceUpdateIntervalId) {
      clearInterval(this.sourceUpdateIntervalId);
    }

    for (const subscription of this.openSubscriptions) {
      subscription.unsubscribe();
    }
    this.openSubscriptions.length = 0;
  }

  // on init
  private addSource() {
    this.addEmptySource();
  }

  // execute on map style change
  public reloadSource() {
    this.addEmptySource();
    if (this.isEnabled) {
      this.loadGeometries();
    }
  }

  public getFeatureCollection(): FeatureCollection {
    return this.routeSegmentGeometries;
  }

  private addEmptySource() {
    this.mapLayersManager.addSource(RouteSegmentStatusSourceService.ROUTE_SEGMENT_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });
  }

  private updateSource() {
    // update source
    if (this.routeConfigurationId) {
      this.locationApiService.getRouteSegmentGeoJson(this.routeConfigurationId, this.routeIds).then(geometries => {
        this.routeSegmentGeometries = geometries;
        this.loadGeometries();
      }).catch(message => {
        console.error('Loading Route Segment Status Geometries failed!');
      });
    } else {
      console.warn('Trying to initialize Route Segment source with no routeConfigurationId!');
    }
  }

  private loadGeometries() {
    if (this.routeSegmentGeometries) {
      this.mapLayersManager
        ?.getGeoJsonSource(RouteSegmentStatusSourceService.ROUTE_SEGMENT_SOURCE_ID)
        ?.setData(this.routeSegmentGeometries);
    }
  }
}
